/** Preenchimento a cheio dos elementos cortados (a parte pura).
 *
 *  Um corte no 3D mostrava as lajes como duas faces soltas: o three.js
 *  deita fora o que fica do lado cortado, e o interior de um sólido é
 *  oco (Rui, 18/10/2026: "é muito estranho ver lajes representadas só
 *  como duas faces, quando as recortamos").
 *
 *  Aqui calcula-se a secção de cada elemento em cada plano, com o mesmo
 *  núcleo das Vistas 2D (`cutTriangles` + `assembleLoops`), por isso o
 *  corte no 3D e a planta dizem a mesma coisa. Elemento a elemento:
 *  um elemento isolado, sem vizinhos, também fica cheio.
 *
 *  Só se preenche o que fecha. Um contorno aberto (malha mal exportada,
 *  superfície sem espessura) fica como estava, só com as faces, e conta
 *  em `abertos`: inventar o fecho dava um preenchimento errado.
 *
 *  Puro, sem cena: recebe triângulos e devolve triângulos. */

import { ShapeUtils, Vector2 } from 'three'

import { assembleLoops } from '@viewer/views2d/lib/assembleLoops'
import { cutTriangles } from '@viewer/views2d/lib/cutTriangles'
import type { Point2, Segment2, Vec3 } from '@viewer/views2d/lib/viewTypes'
import { baseDoPlano } from './desenhoDeCorte'

export interface PlanoDoPreenchimento {
  /** Unitária, e aponta para o lado que fica (convenção do three.js). */
  normal: Vec3
  /** Um ponto do plano, no MESMO referencial dos triângulos. */
  ponto: Vec3
}

/** A secção de um elemento num plano, já em triângulos. */
export interface SeccaoDoElemento {
  localId: number
  /** xyz intercalado, no referencial dos triângulos recebidos. */
  posicoes: Float32Array
  /** Contornos (fechados), para o traço do corte: pares de pontos xyz. */
  contornos: Float32Array
}

export interface ResultadoDoPreenchimento {
  seccoes: SeccaoDoElemento[]
  /** Elementos cortados cujo contorno não fechou: ficam sem preenchimento. */
  abertos: number
}

/** Folga entre o preenchimento e o plano, em metros, para o lado que
 *  fica. Em cima do plano, o próprio plano cortava-o a metade das vezes
 *  (a distância dava zero, com ruído de vírgula flutuante). */
export const FOLGA_DO_PREENCHIMENTO = 0.001

/** Área com sinal (positiva no sentido anti-horário). */
export function areaComSinal(p: readonly Point2[]): number {
  let a = 0
  for (let i = 0; i < p.length; i++) {
    const [x1, y1] = p[i]
    const [x2, y2] = p[(i + 1) % p.length]
    a += x1 * y2 - x2 * y1
  }
  return a / 2
}

/** Ponto dentro de um polígono (par-ímpar). */
export function pontoDentro(pt: Point2, poli: readonly Point2[]): boolean {
  let dentro = false
  for (let i = 0, j = poli.length - 1; i < poli.length; j = i++) {
    const [xi, yi] = poli[i]
    const [xj, yj] = poli[j]
    if (yi > pt[1] !== yj > pt[1] && pt[0] < ((xj - xi) * (pt[1] - yi)) / (yj - yi) + xi) {
      dentro = !dentro
    }
  }
  return dentro
}

/** Um ponto que está mesmo dentro do contorno: o meio de uma aresta,
 *  empurrado um nada para o lado de dentro. O primeiro vértice não
 *  serve, porque pode tocar no contorno de fora (uma laje com uma
 *  coretes encostada à borda). */
function pontoInterior(p: readonly Point2[]): Point2 {
  const sentido = areaComSinal(p) >= 0 ? 1 : -1
  for (let i = 0; i < p.length; i++) {
    const a = p[i]
    const b = p[(i + 1) % p.length]
    const dx = b[0] - a[0]
    const dy = b[1] - a[1]
    const l = Math.hypot(dx, dy)
    if (l < 1e-9) continue
    // A normal para dentro de um contorno anti-horário é (-dy, dx).
    const k = (1e-5 * sentido) / l
    return [(a[0] + b[0]) / 2 - dy * k, (a[1] + b[1]) / 2 + dx * k]
  }
  return p[0]
}

/**
 * Arruma os contornos de UM elemento em polígonos com furos.
 *
 * Cada contorno conta quantos outros o contêm: com um número par é de
 * fora, ímpar é um furo, e o furo pertence ao contorno mais pequeno que
 * o contém e é de fora. É a regra par-ímpar que o SVG das Vistas 2D usa
 * para o poché, por isso dá o mesmo resultado.
 */
export function poligonosComFuros(
  contornos: readonly (readonly Point2[])[],
): Array<{ fora: Point2[]; furos: Point2[][] }> {
  const validos = contornos.filter((c) => c.length >= 3 && Math.abs(areaComSinal(c)) > 1e-10)
  const area = validos.map((c) => Math.abs(areaComSinal(c)))
  const interior = validos.map(pontoInterior)
  const pais = validos.map((_, i) => {
    const contem: number[] = []
    for (let j = 0; j < validos.length; j++) {
      if (j !== i && area[j] > area[i] && pontoDentro(interior[i], validos[j])) contem.push(j)
    }
    return contem
  })
  const out: Array<{ fora: Point2[]; furos: Point2[][] }> = []
  const indiceDeFora = new Map<number, number>()
  validos.forEach((c, i) => {
    if (pais[i].length % 2 === 0) {
      indiceDeFora.set(i, out.length)
      out.push({ fora: [...c], furos: [] })
    }
  })
  validos.forEach((c, i) => {
    if (pais[i].length % 2 === 0) return
    // O pai directo: o mais pequeno dos que o contêm e são de fora.
    let melhor = -1
    for (const j of pais[i]) {
      if (pais[j].length % 2 !== 0) continue
      if (melhor < 0 || area[j] < area[melhor]) melhor = j
    }
    const k = indiceDeFora.get(melhor)
    if (k !== undefined) out[k].furos.push([...c])
  })
  return out
}

/** Triangula polígonos com furos. Devolve os triângulos em 2D. */
export function triangular(
  poligonos: ReadonlyArray<{ fora: Point2[]; furos: Point2[][] }>,
): Point2[] {
  const tri: Point2[] = []
  for (const { fora, furos } of poligonos) {
    const contorno = fora.map(([x, y]) => new Vector2(x, y))
    const buracos = furos.map((f) => f.map(([x, y]) => new Vector2(x, y)))
    // O earcut quer o de fora num sentido e os furos no outro.
    if (ShapeUtils.isClockWise(contorno)) contorno.reverse()
    for (const b of buracos) if (!ShapeUtils.isClockWise(b)) b.reverse()
    const todos = [contorno, ...buracos].flat()
    let faces: number[][]
    try {
      faces = ShapeUtils.triangulateShape(contorno, buracos)
    } catch {
      continue
    }
    for (const f of faces) {
      for (const i of f) tri.push([todos[i].x, todos[i].y])
    }
  }
  return tri
}

/**
 * A secção de um conjunto de elementos num plano.
 *
 * `itens`: triângulos de cada elemento (os da recolha das Vistas 2D,
 * `collectWorldTriangles`), no mesmo referencial do `plano`. O
 * resultado vem nesse referencial, já afastado do plano pela
 * `FOLGA_DO_PREENCHIMENTO` para o lado que fica.
 */
export function seccaoNoPlano(
  itens: ReadonlyArray<{ localId: number; positions: Float32Array; indices: Uint32Array }>,
  plano: PlanoDoPreenchimento,
  tol = 1e-4,
): ResultadoDoPreenchimento {
  const n = plano.normal
  const [u, w] = baseDoPlano(n)
  const o = plano.ponto
  const seccoes: SeccaoDoElemento[] = []
  let abertos = 0
  const para3D = (p: Point2): Vec3 => [
    o[0] + u[0] * p[0] + w[0] * p[1] + n[0] * FOLGA_DO_PREENCHIMENTO,
    o[1] + u[1] * p[0] + w[1] * p[1] + n[1] * FOLGA_DO_PREENCHIMENTO,
    o[2] + u[2] * p[0] + w[2] * p[1] + n[2] * FOLGA_DO_PREENCHIMENTO,
  ]
  for (const item of itens) {
    const segs3 = cutTriangles(item.positions, item.indices, { origin: [...o] as Vec3, normal: [...n] as Vec3 })
    if (segs3.length === 0) continue
    const segs2: Segment2[] = segs3.map((s) => {
      const a: Vec3 = [s.a[0] - o[0], s.a[1] - o[1], s.a[2] - o[2]]
      const b: Vec3 = [s.b[0] - o[0], s.b[1] - o[1], s.b[2] - o[2]]
      return {
        a: [a[0] * u[0] + a[1] * u[1] + a[2] * u[2], a[0] * w[0] + a[1] * w[1] + a[2] * w[2]],
        b: [b[0] * u[0] + b[1] * u[1] + b[2] * u[2], b[0] * w[0] + b[1] * w[1] + b[2] * w[2]],
      }
    })
    const { loops, open } = assembleLoops(segs2, tol)
    if (loops.length === 0) {
      if (open.length > 0) abertos++
      continue
    }
    const tri = triangular(poligonosComFuros(loops.map((l) => l.points)))
    if (tri.length === 0) continue
    const posicoes = new Float32Array(tri.length * 3)
    tri.forEach((p, i) => posicoes.set(para3D(p), i * 3))
    const linhas: number[] = []
    for (const l of loops) {
      for (let i = 0; i < l.points.length; i++) {
        linhas.push(...para3D(l.points[i]), ...para3D(l.points[(i + 1) % l.points.length]))
      }
    }
    seccoes.push({ localId: item.localId, posicoes, contornos: new Float32Array(linhas) })
  }
  return { seccoes, abertos }
}

/** O elemento atravessa o plano? Pelas oito esquinas da caixa. */
export function caixaAtravessa(
  min: Vec3,
  max: Vec3,
  plano: PlanoDoPreenchimento,
): boolean {
  let neg = false
  let pos = false
  for (let i = 0; i < 8; i++) {
    const x = i & 1 ? max[0] : min[0]
    const y = i & 2 ? max[1] : min[1]
    const z = i & 4 ? max[2] : min[2]
    const d =
      (x - plano.ponto[0]) * plano.normal[0] +
      (y - plano.ponto[1]) * plano.normal[1] +
      (z - plano.ponto[2]) * plano.normal[2]
    if (d < 0) neg = true
    else pos = true
    if (neg && pos) return true
  }
  return false
}
